import {
  AllFeatures,
  applyFeatures,
  createForm,
  FormJSEnhancer,
} from '@smartsheet-dataflow/formjs';
import { expressionEnhancer } from '@smartsheet-dataflow/formjs-expressions';
import {
  ComponentFeature,
  FormProvider,
} from '@smartsheet-dataflow/formjs-react';
import React, { useRef } from 'react';
import { useSelector } from 'react-redux';
import { applyMiddleware, Middleware, Store } from 'redux';
import { logger } from 'redux-logger';
import {
  nodesSelector,
  WorkflowStateNode,
} from '../../services/ducks/workflowDuck/workflowDuck';
import { bridgeSchemaEnhancer } from '../../services/forms/BridgeSchemaEnhancer';
import {
  SchemaFeature,
  IsOpenFeature,
  ArrayItemsLabelFeature,
  ErrorFeature,
  errorSelector,
  setErrorAction,
} from '../../services/forms/features';
import { ParamModel } from '../../services/forms/params/ParamModelTypes';
import { ParamSchema } from '../../services/forms/params/ParamSchemaTypes';
import { withFormJSChildren } from '../FormJS/WithFormJSChildren/withFormJSChildren';
import { withFormJSEnumsFromChildren } from '../FormJS/WithFormJSEnumsFromChildren/withFormJSEnumsFromChildren';
import { withFormJSIsOpen } from '../FormJS/WithFormJSIsOpen/withFormJSIsOpen';
import { withFormJSOptionsFromChildren } from '../FormJS/WithFormJSOptionsFromChildren/withFormJSOptionsFromChildren';
import { withFormJSDefaults } from '../FormJS/withFormJSDefaults';
import { labelValidator } from '../features/RightRail/EditModule/utils';
import {
  InputCheckbox,
  InputGroup,
  InputGroupArray,
  InputMap,
  InputMultiSelect,
  InputNumber,
  InputSelect,
  InputStringArray,
  InputTextArea,
} from '../inputs';
import {
  ErrorMessageInputText,
  InputTextProps,
} from '../inputs/ErrorMessageInputText';
import { InputWorkflow } from '../inputs/InputWorkflow';
import { WithDefaultDecorator } from '../inputs/decorators';

const FormJSFeatures = applyFeatures(
  ...(AllFeatures as any),
  ComponentFeature,
  SchemaFeature,
  IsOpenFeature,
  ArrayItemsLabelFeature,
  ErrorFeature
);

const ErrorMessageInputTextAdaptor =
  (nodes: Record<string, WorkflowStateNode>) =>
  ({ id, value, label, title, readonly, onChange }: InputTextProps) =>
    ErrorMessageInputText({
      id,
      value,
      label,
      title,
      readonly,
      onChange,
      validator: labelValidator(nodes),
    });

export interface DesignerFormProps {
  schema: ParamSchema[];
  model: ParamModel;
  handler: (state: any) => void;
  validation?: (value: string) => boolean;
}

const validationMiddleware =
  (validationFn?: (value: string) => boolean): Middleware =>
  api =>
  next =>
  action => {
    if (validationFn !== undefined) {
      if (
        action.type === 'SET_VALUE' &&
        action.payload.inputId === 'form.tmpname'
      ) {
        const error = errorSelector(action.payload.inputId)(api.getState());
        if (!validationFn(action.payload.value)) {
          const errorMessage = action.payload.value.length
            ? 'A state name must be unique.'
            : 'A state name must not be blank';
          setErrorAction(api.dispatch)({
            inputId: action.payload.inputId,
            error: errorMessage,
          });
        } else if (error !== undefined && error !== '') {
          setErrorAction(api.dispatch)({
            inputId: action.payload.inputId,
            error: '',
          });
        }
      }
    }
    return next(action);
  };

export const DesignerForm = ({
  schema,
  model,
  handler,
  validation,
}: DesignerFormProps) => {
  const nodes = useSelector(nodesSelector);

  const inputComponents = useRef({
    STRING: withFormJSDefaults(ErrorMessageInputTextAdaptor(nodes)),
    TEXT: withFormJSDefaults(WithDefaultDecorator(InputTextArea)),
    NUMBER: withFormJSDefaults(WithDefaultDecorator(InputNumber)),
    ENUM: withFormJSEnumsFromChildren(
      withFormJSDefaults(WithDefaultDecorator(InputSelect))
    ),
    BOOLEAN: withFormJSDefaults(WithDefaultDecorator(InputCheckbox)),
    MAP: withFormJSIsOpen(withFormJSDefaults(InputMap)),
    GROUP: withFormJSIsOpen(withFormJSChildren(withFormJSDefaults(InputGroup))),
    STRING_ARRAY: withFormJSDefaults(WithDefaultDecorator(InputStringArray)),
    GROUP_ARRAY: withFormJSChildren(withFormJSDefaults(InputGroupArray)),
    'BRIDGE.WORKFLOW': withFormJSDefaults(WithDefaultDecorator(InputWorkflow)),
    ENUM_ARRAY: withFormJSOptionsFromChildren(
      withFormJSDefaults(WithDefaultDecorator(InputMultiSelect))
    ),
  });

  const bridgeSchema = bridgeSchemaEnhancer(handler, schema, model);

  const expressions = expressionEnhancer({
    loadExpressions: state => state.hideExpression,
  });

  const middleware = applyMiddleware(logger, validationMiddleware(validation));

  const form = createForm(
    bridgeSchema as FormJSEnhancer,
    expressions,
    middleware as FormJSEnhancer,
    FormJSFeatures as FormJSEnhancer
  ) as Store;

  return <FormProvider form={form} components={inputComponents.current} />;
};
